import React, { useState, useEffect } from 'react';
import {
  CalendarCheck,
  IndianRupee,
  Receipt,
  Banknote,
  Smartphone,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  X,
  Lock,
  Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import dayCloseService from '../../../services/dayCloseService';

const fmt = (val) =>
  Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function DayCloseModal({ onClose }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [notes, setNotes] = useState('');
  const [actualCash, setActualCash] = useState('');

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await dayCloseService.getSummary();
        setSummary(res.data || res);
      } catch {
        toast.error('Failed to load day close summary');
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  const handleExecute = async () => {
    if (!summary) return;
    const cashVal =
      summary.cashHandoverEnabled && actualCash !== ''
        ? parseFloat(actualCash)
        : null;
    setExecuting(true);
    try {
      const res = await dayCloseService.executeDayClose(
        summary.businessDate,
        notes,
        cashVal
      );
      setSummary(res.data || res);
      setShowConfirm(false);
      toast.success('Day closed successfully!');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        'Failed to close day';
      toast.error(msg);
    } finally {
      setExecuting(false);
    }
  };

  const isClosed = summary?.status === 'CLOSED';
  const shiftEnabled = summary?.shiftEnabled === true;
  const hasOpenShifts = (summary?.openShiftCount || 0) > 0;
  const hasRunningOrders = (summary?.runningOrderCount || 0) > 0;
  const hasBlockers = (summary?.openBillCount || 0) > 0 || hasRunningOrders || (shiftEnabled && hasOpenShifts);
  const canClose = !isClosed && !hasBlockers;

  const cashHandoverEnabled = summary?.cashHandoverEnabled === true;
  const cashDiff =
    actualCash !== '' ? parseFloat(actualCash) - Number(summary?.cashTotal || 0) : null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-xl">
              <CalendarCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Close Day</h2>
              {summary?.businessDate && (
                <p className="text-xs text-slate-500">
                  {new Date(summary.businessDate + 'T00:00:00').toLocaleDateString('en-IN', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {summary && (
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                  isClosed
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}
              >
                {isClosed ? 'Closed' : 'Open'}
              </span>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-500">Loading summary...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniCard icon={IndianRupee} label="Revenue" value={`₹${fmt(summary?.totalRevenue)}`} />
                <MiniCard icon={Receipt} label="Bills" value={summary?.totalBills ?? 0} />
                <MiniCard icon={Banknote} label="Cash" value={`₹${fmt(summary?.cashTotal)}`} />
                <MiniCard icon={Smartphone} label="Online" value={`₹${fmt(summary?.onlineTotal)}`} />
              </div>

              {/* Shift Breakdown */}
              {summary?.shifts?.length > 0 && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Shifts</span>
                    {hasOpenShifts && (
                      <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                        {summary.openShiftCount} open
                      </span>
                    )}
                  </div>
                  <div className="divide-y divide-slate-200">
                    {summary.shifts.map((s) => (
                      <div key={s.shiftId} className="px-4 py-2.5 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${s.status === 'CLOSED' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                          <span className="font-medium text-slate-700">{s.userName || '—'}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>{s.startedAt ? new Date(s.startedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}</span>
                          <span className="font-semibold text-slate-700">₹{fmt(s.totalSales)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {!isClosed && (
                <div className="space-y-3">
                  {hasRunningOrders && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2.5">
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-700">
                        <span className="font-semibold">{summary.runningOrderCount} order(s) in progress</span> — bill or cancel them before closing.
                      </p>
                    </div>
                  )}
                  {(summary?.openBillCount || 0) > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2.5">
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-700">
                        <span className="font-semibold">{summary.openBillCount} open bill(s)</span> must be finalized before closing.
                      </p>
                    </div>
                  )}
                  {shiftEnabled && hasOpenShifts && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2.5">
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <p className="text-xs text-amber-700">
                        <span className="font-semibold">{summary.openShiftCount} open shift(s)</span> must be closed first.
                      </p>
                    </div>
                  )}
                  {canClose && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <p className="text-xs text-emerald-700 font-medium">All clear — ready to close.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Already Closed */}
              {isClosed && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-2.5">
                  <Lock className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <div className="text-xs text-emerald-700">
                    <span className="font-semibold">Day closed</span>
                    {summary?.closedBy && ` by ${summary.closedBy}`}
                    {summary?.closedAt && ` at ${new Date(summary.closedAt).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, day: 'numeric', month: 'short' })}`}
                  </div>
                </div>
              )}

              {/* Confirm Section */}
              {showConfirm && !isClosed && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-4">
                  {/* Cash Handover */}
                  {cashHandoverEnabled && (
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Cash Handover</p>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Expected Cash</span>
                        <span className="font-semibold text-slate-800">₹{fmt(summary?.cashTotal)}</span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                          Actual Cash <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={actualCash}
                          onChange={(e) => setActualCash(e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                        />
                      </div>
                      {cashDiff !== null && !isNaN(cashDiff) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Difference</span>
                          <span className={`font-semibold ${cashDiff === 0 ? 'text-emerald-600' : cashDiff < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                            ₹{fmt(cashDiff)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Any remarks..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 resize-none"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition"
            >
              Cancel
            </button>
            {!isClosed && (
              showConfirm ? (
                <button
                  onClick={handleExecute}
                  disabled={executing || (cashHandoverEnabled && actualCash === '')}
                  className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition flex items-center gap-2 disabled:opacity-60"
                >
                  {executing && <Loader2 className="w-4 h-4 animate-spin" />}
                  {executing ? 'Closing...' : 'Confirm & Close'}
                </button>
              ) : (
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={!canClose}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
                    canClose
                      ? 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Close Day
                </button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-sm font-bold text-slate-800 truncate">{value}</p>
    </div>
  );
}
